/**
 * 조감도(오버뷰) — 크라임씬 방송이 세트를 위에서 보여주는 그 화면.
 *
 * 천장을 걷고 카메라를 건물 위로 올린 다음, 방 이름을 화면 좌표에 얹는다.
 * 라벨은 DOM 이다. 3D 텍스트로 그리면 각도에 따라 읽히지 않고, 이 화면의
 * 목적은 '읽히는 것' 하나뿐이다.
 *
 * 여섯 사람이 12분 안에 조사를 나눠야 하는 게임이라, 어디에 무엇이 있는지
 * 한 화면에 보이는 것이 규칙 설명보다 빠르다.
 */

import * as THREE from 'three';
import { ep } from './episodeData';

export class Overview {
  enabled = false;
  private layer: HTMLDivElement;
  private labels: HTMLDivElement[] = [];
  private v = new THREE.Vector3();

  constructor() {
    this.layer = document.createElement('div');
    this.layer.className = 'ov-layer';
    this.layer.style.display = 'none';
    document.body.appendChild(this.layer);

    for (const r of ep().ROOMS) {
      const el = document.createElement('div');
      el.className = 'ov-label';
      el.textContent = r.name;
      this.layer.appendChild(el);
      this.labels.push(el);
    }
  }

  toggle() {
    this.enabled = !this.enabled;
    this.layer.style.display = this.enabled ? 'block' : 'none';
    // 조감도는 '한눈에 보는' 화면이다. 대화 로그가 세트를 가리면 목적을 잃는다
    document.body.classList.toggle('ov-on', this.enabled);
    return this.enabled;
  }

  /** 건물 전체가 화면에 들어오는 높이와 각도. 방 좌표에서 직접 구한다 */
  place(camera: THREE.PerspectiveCamera) {
    let x1 = Infinity;
    let x2 = -Infinity;
    let z1 = Infinity;
    let z2 = -Infinity;
    for (const r of ep().ROOMS) {
      x1 = Math.min(x1, r.x1);
      x2 = Math.max(x2, r.x2);
      z1 = Math.min(z1, r.z1);
      z2 = Math.max(z2, r.z2);
    }
    const cx = (x1 + x2) / 2;
    const cz = (z1 + z2) / 2;
    const w = x2 - x1;
    const d = z2 - z1;

    // 세로 화각으로 폭을 담으려면 가로 화각부터 본다
    const vFov = (camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
    // 완전한 수직이면 입체감이 없다. 살짝 눕혀서 벽이 보이게 한다
    const tilt = 1.24; // 라디안. 수평에서 71도

    // 지면이 시선과 비스듬하면 화면에 담기는 깊이가 화각 계산보다 짧아진다.
    // sin(tilt) 로 나눠서 그만큼 물러난다. 벽 높이도 화면을 먹으므로 여유를 둔다.
    const needW = w / 2 / Math.tan(hFov / 2);
    const needD = d / 2 / Math.tan(vFov / 2) / Math.sin(tilt);
    const dist = Math.max(needW, needD) * 1.35;
    camera.position.set(cx, Math.sin(tilt) * dist, cz + Math.cos(tilt) * dist);
    camera.rotation.set(0, 0, 0);
    camera.rotateX(-tilt);
    // 먼 코너가 far 평면 밖으로 나가면 통째로 사라진다
    if (camera.far < dist * 1.8) {
      camera.far = dist * 1.8;
      camera.updateProjectionMatrix();
    }
  }

  update(camera: THREE.PerspectiveCamera, width: number, height: number) {
    if (!this.enabled) return;
    ep().ROOMS.forEach((r, i) => {
      this.v.set((r.x1 + r.x2) / 2, 1.2, (r.z1 + r.z2) / 2).project(camera);
      const el = this.labels[i];
      if (this.v.z > 1) {
        el.style.display = 'none';
        return;
      }
      el.style.display = 'block';
      el.style.left = `${((this.v.x + 1) / 2) * width}px`;
      el.style.top = `${((-this.v.y + 1) / 2) * height}px`;
    });
  }
}
